import React, { useState, useEffect } from 'react';
import { Container, Title, Text, Card, Group, Button, TextInput, NumberInput, Select, Stack, Alert, CopyButton, ActionIcon, Tooltip } from '@mantine/core';
import { IconCopy, IconQrcode, IconDownload, IconRefresh } from '@tabler/icons-react';
import QRCode from 'qrcode';
import { PaymentQRProps, PaymentMethod } from './PaymentQR.types';
import { QRContainer, QRCodeCanvas, PaymentForm, PaymentInfo } from './PaymentQR.styles';
import { PAYMENT_AMOUNT, generateBankTransferQR, generateSBPQR } from '@/constants/paymentDetails';

export const PaymentQR: React.FC<PaymentQRProps> = ({
  amount = PAYMENT_AMOUNT,
  description = '',
  paymentMethod = 'bank',
  onAmountChange,
  onDescriptionChange,
  onPaymentMethodChange,
  showForm = true
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Получение информации о платеже (для будущего использования)
  // const paymentInfo = getPaymentInfo(amount);

  // Генерация QR-кода
  const generateQRCode = async (data: string) => {
    try {
      setIsGenerating(true);
      setError(null);
      
      const canvas = document.createElement('canvas');
      await QRCode.toCanvas(canvas, data, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      
      const dataUrl = canvas.toDataURL('image/png');
      setQrCodeDataUrl(dataUrl);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError('Ошибка генерации QR-кода');
    } finally {
      setIsGenerating(false);
    }
  };

  // Обновление QR-кода при изменении параметров
  useEffect(() => {
    if (amount > 0) {
      let qrData = '';
      
      if (paymentMethod === 'sbp') {
        qrData = generateSBPQR(amount);
      } else if (paymentMethod === 'bank') {
        qrData = generateBankTransferQR(amount);
      }
      
      if (qrData) {
        generateQRCode(qrData);
      }
    }
  }, [amount, description, paymentMethod]);

  // Скачивание QR-кода
  const downloadQRCode = () => {
    if (qrCodeDataUrl) {
      const link = document.createElement('a');
      link.download = `payment-qr-${amount}-${Date.now()}.png`;
      link.href = qrCodeDataUrl;
      link.click();
    }
  };

  // Копирование данных для оплаты
  const getPaymentData = (): string => {
    if (paymentMethod === 'sbp') {
      return generateSBPQR(amount);
    } else if (paymentMethod === 'bank') {
      return generateBankTransferQR(amount);
    }
    return '';
  };

  return (
    <Container size="md" py="xl">
      <Title order={2} mb="xl" ta="center">
        QR-код для оплаты
      </Title>

      {showForm && (
        <PaymentForm>
          <Stack gap="md">
            <NumberInput
              label="Сумма (руб.)"
              placeholder="Введите сумму"
              value={amount}
              onChange={(value) => onAmountChange?.(Number(value) || 0)}
              min={1}
              max={999999}
              required
            />

            <TextInput
              label="Назначение платежа"
              placeholder="Например: Оплата за техосмотр"
              value={description}
              onChange={(e) => onDescriptionChange?.(e.target.value)}
              required
            />

            <Select
              label="Способ оплаты"
              placeholder="Выберите способ оплаты"
              value={paymentMethod}
              onChange={(value) => onPaymentMethodChange?.(value as PaymentMethod)}
              data={[
                { value: 'sbp', label: 'СБП (Система быстрых платежей)' },
                { value: 'bank', label: 'Банковский перевод' }
              ]}
            />
          </Stack>
        </PaymentForm>
      )}

      {error && (
        <Alert icon={<IconQrcode size={16} />} title="Ошибка" color="red" mb="md">
          {error}
        </Alert>
      )}

      {amount > 0 && (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack align="center" gap="md">
            <Title order={3} ta="center">
              QR-код для оплаты
            </Title>
            
            <Text size="sm" c="dimmed" ta="center">
              {paymentMethod === 'sbp' ? 'СБП' : 'Банковский перевод'} • {amount} ₽
            </Text>

            {isGenerating ? (
              <Group>
                <IconRefresh className="animate-spin" />
                <Text>Генерация QR-кода...</Text>
              </Group>
            ) : qrCodeDataUrl ? (
              <QRContainer>
                <QRCodeCanvas src={qrCodeDataUrl} alt="QR код для оплаты" />
              </QRContainer>
            ) : null}

            {qrCodeDataUrl && (
              <Group gap="sm">
                <CopyButton value={getPaymentData()}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Скопировано!' : 'Копировать данные'}>
                      <ActionIcon color={copied ? 'teal' : 'blue'} onClick={copy}>
                        <IconCopy size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>

                <Button
                  leftSection={<IconDownload size={16} />}
                  onClick={downloadQRCode}
                  color="green"
                >
                  Скачать QR
                </Button>
              </Group>
            )}

            <PaymentInfo>
              <Text size="sm" c="dimmed" ta="center">
                Отсканируйте QR-код в приложении банка для быстрой оплаты
              </Text>
            </PaymentInfo>
          </Stack>
        </Card>
      )}

      {amount === 0 && (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text ta="center" c="dimmed">
            Введите сумму для генерации QR-кода
          </Text>
        </Card>
      )}
    </Container>
  );
};

export default PaymentQR;
